import { z } from "zod";

export const PantryUnitSchema = z.enum(["g", "kg", "ml", "l", "szt", "opak", "łyż", "szkl"]);
export type PantryUnit = z.infer<typeof PantryUnitSchema>;

export const PantryCategorySchema = z.enum([
  "nabiał",
  "mięso",
  "warzywa",
  "owoce",
  "pieczywo",
  "suche",
  "napoje",
  "mrożone",
  "słodycze",
  "przyprawy",
  "inne",
]);
export type PantryCategory = z.infer<typeof PantryCategorySchema>;

export const PantrySourceSchema = z.enum(["manual", "photo", "voice", "barcode", "receipt"]);
export type PantrySource = z.infer<typeof PantrySourceSchema>;

// Single pantry item — what's in the fridge / cupboard.
export const PantryItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  quantity: z.number().nullable(),
  unit: PantryUnitSchema.nullable(),
  category: PantryCategorySchema.nullable(),
  isStaple: z.boolean(),
  expiresAt: z.number().int().nullable(),
  source: PantrySourceSchema,
  createdAt: z.number().int(),
  updatedAt: z.number().int(),
});
export type PantryItemView = z.infer<typeof PantryItemSchema>;

// Parsed item suggestion — shape we expect from AI photo / text / voice parsers.
export const PantryItemDraftSchema = z.object({
  name: z.string().min(1).max(200),
  quantity: z.number().min(0).max(10000).nullable(),
  unit: PantryUnitSchema.nullable(),
  category: PantryCategorySchema.nullable(),
  isStaple: z.boolean(),
  expiresAt: z.number().int().nullable(),
  confidence: z.number().min(0).max(1),
});
export type PantryItemDraft = z.infer<typeof PantryItemDraftSchema>;

export const PantryParseResponseSchema = z.object({
  items: z.array(PantryItemDraftSchema).max(40),
});
export type PantryParseResponse = z.infer<typeof PantryParseResponseSchema>;

// Manual single-item add.
export const PantryCreateSchema = z.object({
  name: z.string().trim().min(1).max(200),
  quantity: z.number().min(0).max(10000).nullable().optional(),
  unit: PantryUnitSchema.nullable().optional(),
  category: PantryCategorySchema.nullable().optional(),
  isStaple: z.boolean().optional(),
  expiresAt: z.number().int().nullable().optional(),
});
export type PantryCreate = z.infer<typeof PantryCreateSchema>;

// Bulk save after photo/voice review.
export const PantryBulkCreateSchema = z.object({
  items: z.array(PantryItemDraftSchema).min(1).max(40),
  source: PantrySourceSchema.default("manual"),
  rawInputText: z.string().max(2000).optional(),
});
export type PantryBulkCreate = z.infer<typeof PantryBulkCreateSchema>;

// Partial update (edit inline).
export const PantryUpdateSchema = z.object({
  name: z.string().min(1).max(200).optional(),
  quantity: z.number().min(0).max(10000).nullable().optional(),
  unit: PantryUnitSchema.nullable().optional(),
  category: PantryCategorySchema.nullable().optional(),
  isStaple: z.boolean().optional(),
  expiresAt: z.number().int().nullable().optional(),
});
export type PantryUpdate = z.infer<typeof PantryUpdateSchema>;

// Input for POST /pantry/photo — base64 fridge image.
export const PantryPhotoInputSchema = z.object({
  imageDataUrl: z
    .string()
    .max(8_000_000, "Zdjęcie za duże, zmniejsz rozdzielczość")
    .regex(/^data:image\/(jpeg|jpg|png|webp);base64,/, "Nieprawidłowy format obrazu"),
});
export type PantryPhotoInput = z.infer<typeof PantryPhotoInputSchema>;
